import React from "react";
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Box,
  Typography,
} from "@mui/material";
import {
  EmojiEvents as TrophyIcon,
  StarBorder as StarIcon,
  Circle as CircleIcon,
  Female as FemaleIcon,
} from "@mui/icons-material";
import { Division, getLevelRangeLabel } from "../types";
import { useDivision } from "../contexts/DivisionContext";
import { useOrganization } from "../contexts/OrganizationContext";

interface DivisionSelectorProps {
  variant?: "outlined" | "filled" | "standard";
  size?: "small" | "medium";
  showIcon?: boolean;
  fullWidth?: boolean;
  sx?: any;
  excludeTest?: boolean;
  excludeUnassigned?: boolean;
  useAllDivisions?: boolean; // Show every division across all seasons (for admin)
  hideLabel?: boolean;
}

const getDivisionIcon = (division: Division) => {
  switch (division) {
    case "unassigned":
      return <CircleIcon sx={{ fontSize: 18, color: "#757575" }} />;
    case "Master":
      return <TrophyIcon sx={{ fontSize: 18, color: "#ffd700" }} />;
    case "Division 1":
      return <StarIcon sx={{ fontSize: 18, color: "#c0c0c0" }} />;
    case "Division 2":
      return <CircleIcon sx={{ fontSize: 18, color: "#cd7f32" }} />;
    case "Division 2A":
      return <CircleIcon sx={{ fontSize: 18, color: "#b5651d" }} />;
    case "Division 2B":
      return <CircleIcon sx={{ fontSize: 18, color: "#a0522d" }} />;
    case "Division 3":
      return <CircleIcon sx={{ fontSize: 18, color: "#8b4513" }} />;
    case "Division 4":
      return <CircleIcon sx={{ fontSize: 18, color: "#5d4037" }} />;
    case "Women Division":
      return <FemaleIcon sx={{ fontSize: 18, color: "#e91e63" }} />;
    case "Women Division 2":
      return <FemaleIcon sx={{ fontSize: 18, color: "#f06292" }} />;
    case "Test":
      return <CircleIcon sx={{ fontSize: 18, color: "#9e9e9e" }} />;
    case "All":
      return <TrophyIcon sx={{ fontSize: 18, color: "#1976d2" }} />;
    default:
      return null;
  }
};

const getDivisionDisplayName = (division: Division, isPE = false): string => {
  if (isPE) {
    const range = getLevelRangeLabel([division]);
    if (range) return `${division}: ${range}`;
  }
  if (division === "Women Division") return "Women Division 1";
  if (division === "unassigned") return "Unassigned";
  if (division === "All") return "Show All";
  return division;
};

const getDivisionColor = (division: Division, isPE = false) => {
  if (isPE) return "#743444";
  switch (division) {
    case "unassigned":
      return "#757575";
    case "Master":
      return "#ffd700";
    case "Division 1":
      return "#c0c0c0";
    case "Division 2":
      return "#cd7f32";
    case "Division 2A":
      return "#b5651d";
    case "Division 2B":
      return "#a0522d";
    case "Division 3":
      return "#8b4513";
    case "Women Division":
      return "#e91e63";
    case "Women Division 2":
      return "#f06292";
    case "Test":
      return "#9e9e9e";
    case "All":
      return "#1976d2";
    default:
      return "#1976d2";
  }
};

export default function DivisionSelector({
  variant = "outlined",
  size = "medium",
  showIcon = true,
  fullWidth = false,
  sx = {},
  excludeTest = false,
  excludeUnassigned = false,
  useAllDivisions = false,
  hideLabel = false,
}: DivisionSelectorProps) {
  const { currentDivision, setCurrentDivision, divisions, allDivisions } = useDivision();
  const { currentOrg } = useOrganization();
  const isPadelEagle = currentOrg?.slug === "padel-eagle";
  const labelText = isPadelEagle ? "Level" : "Division";

  // Filter out Test and/or unassigned divisions based on props
  const filteredDivisions = (useAllDivisions ? allDivisions : divisions).filter((d) => {
    if (excludeTest && d === "Test") return false;
    if (excludeUnassigned && d === "unassigned") return false;
    return true;
  });

  return (
    <FormControl
      variant={variant}
      size={size}
      fullWidth={fullWidth}
      sx={{ minWidth: 160, ...sx }}
    >
      {!hideLabel && (
        <InputLabel id="division-selector-label">{labelText}</InputLabel>
      )}
      <Select
        labelId="division-selector-label"
        value={currentDivision}
        label={hideLabel ? undefined : labelText}
        onChange={(e) => setCurrentDivision(e.target.value as Division)}
        renderValue={(selected) => (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {showIcon && getDivisionIcon(selected)}
            <Typography
              variant="body2"
              sx={{ color: getDivisionColor(selected, isPadelEagle), fontWeight: "bold" }}
            >
              {getDivisionDisplayName(selected, isPadelEagle)}
            </Typography>
          </Box>
        )}
      >
        {filteredDivisions.map((division) => (
          <MenuItem key={division} value={division}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                width: "100%",
              }}
            >
              {showIcon && getDivisionIcon(division)}
              <Typography
                sx={{
                  color: getDivisionColor(division, isPadelEagle),
                  fontWeight: currentDivision === division ? "bold" : "normal",
                }}
              >
                {getDivisionDisplayName(division, isPadelEagle)}
              </Typography>
              {currentDivision === division && (
                <Chip
                  label="Current"
                  size="small"
                  color="primary"
                  sx={{ ml: "auto", height: 20 }}
                />
              )}
            </Box>
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}
